import { StaleSessionError, isSaveOutcomeUnknown, type Ticket, type UpdateTicketData } from '@field-tickets/shared';
import { initializeTicketQueueTab, isTicketQueueTabReady, nextTicketQueueSequence, observeOwnTicketQueueEntry, ticketQueueTabId, withDepartedTicketQueueTab } from '@/utils/offlineQueueTab';
import { getAuthEpoch, isCurrentAuthEpoch } from '@/utils/authEpoch';
import { isStorageQuotaError, pruneExpiredTicketStorage, writeTicketStorage } from '@/utils/ticketStorageRetention';

/**
 * Offline autosave queue for ticket edits (web / PWA only).
 *
 * A field tech on a flaky connection must never lose a ticket edit just because
 * the autosave PUT failed while offline. This is a minimal IndexedDB-backed
 * journal of immutable admissions per owner, ticket, tab and sequence.
 * Another tab's edit is never overwritten or removed by a completion. The autosave path
 * enqueues here only on a network/offline failure (never on a 4xx validation
 * error, which must still surface as an error), and a replay pass PUTs the
 * queued payload when connectivity returns.
 *
 * No runtime dependency (no idb / idb-keyval) — a thin wrapper over the raw
 * IndexedDB API is enough for a single keyed store. apps/mobile has its own
 * SQLite offline layer and is intentionally untouched.
 */

const DB_NAME = 'alturex-tickets-offline';
const DB_VERSION = 4;
const DB_OPEN_TIMEOUT_MS = 5_000;
const STORE = 'ticket-save-queue';
const QUARANTINE_STORE = 'legacy-ticket-save-quarantine';
const FAILURE_STORE = 'ticket-save-failures';
const RECEIPT_STORE = 'ticket-save-receipts';
const ENTRY_PREFIX = 'alturex-tickets-offline:entry:';
const FAILURE_PREFIX = 'alturex-tickets-offline:failure:';
const RECEIPT_PREFIX = 'alturex-tickets-offline:receipt:';
/** All owners' queue/mirror/quarantine records expire 30 days after their last write. */
export const OFFLINE_SAVE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface QuarantinedTicketSave {
  id: string;
  tabId: string;
  sequence: number;
  baseRevision?: number;
  ticketId: string;
  payload: UpdateTicketData;
  baseSnapshot?: TicketConflictSnapshot;
  updatedAt: number;
}

export interface QueuedTicketSave {
  owner: string;
  ticketId: string;
  payload: UpdateTicketData;
  /** Server snapshot that the payload's version token describes. */
  baseSnapshot?: TicketConflictSnapshot;
  /** A reached-server rejection stays durable until the user edits/retries it. */
  failure?: QueuedTicketSaveFailure;
  /** Stable tab identity; admissions in different tabs never share a key. */
  tabId: string;
  /** The original server revision sent with this entry's optimistic save. */
  baseRevision?: number;
  /** Monotonic within one tab; retries never allocate again. */
  sequence: number;
  /** Original wall time, used only for display, replay ordering and retention. */
  updatedAt: number;
  /** Exact earlier admissions incorporated into this immutable draft. */
  incorporatedAdmissions?: TicketSaveAdmissionIdentity[];
}

export type TicketSaveAdmissionIdentity = Pick<QueuedTicketSave, 'owner' | 'ticketId' | 'tabId' | 'sequence'>;

export interface QueuedTicketSaveFailure {
  failedAt: number;
  message: string;
  status?: number;
  code?: string;
}

export type TicketConflictSnapshot = Record<string, unknown>;

/**
 * The only ticket fields a background system write may change without making
 * an in-progress form draft unsafe to reapply. `state` and `county` are the
 * persisted geocoder outputs; `updated_at` is the OCC token/DB trigger output.
 */
export const SYSTEM_OWNED_TICKET_REBASE_FIELDS = [
  'state',
  'county',
  'updated_at',
] as const;

const SYSTEM_OWNED_TICKET_REBASE_FIELD_SET = new Set<string>(
  SYSTEM_OWNED_TICKET_REBASE_FIELDS,
);

// Fields the ticket form can overwrite, plus status/lock state that changes
// whether that overwrite is valid. Derived display/permission/rollup fields are
// intentionally excluded because this full-snapshot PUT never writes them.
const TICKET_CONFLICT_FIELDS = [
  'status',
  'is_locked',
  'work_date',
  'description',
  'customer_id',
  'customer_name',
  'bill_to_customer_id',
  'bill_to_customer_name',
  'is_labor_only',
  'coordinates',
  'coordinates_accuracy_m',
  'coordinates_source',
  'state',
  'county',
  'charge_code',
  'billing_requisitioner',
  'billing_afe',
  'billing_notes',
  'work_contact_name',
  'work_contact_phone',
  'needs_work_order',
  'is_ine',
  'labor_entries',
  'member_ids',
  'material_entries',
  'equipment',
  'updated_at',
] as const;

function normalizeConflictValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeConflictValue);
  if (!value || typeof value !== 'object') return value ?? null;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([key]) => key !== '_clientId')
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, nested]) => [key, normalizeConflictValue(nested)]),
  );
}

export function createTicketConflictSnapshot(ticket: Ticket): TicketConflictSnapshot {
  const source = ticket as Ticket & Record<string, unknown>;
  return Object.fromEntries(TICKET_CONFLICT_FIELDS.map((field) => {
    if (field === 'member_ids') {
      const ids = ticket.member_ids ?? (ticket.members || [])
        .map((member) => member.employee_id)
        .filter(Boolean);
      return [field, normalizeConflictValue(ids)];
    }
    if (field === 'bill_to_customer_name') {
      return [field, normalizeConflictValue(
        ticket.bill_to_customer_name ?? ticket.bill_to_customer_name_snapshot,
      )];
    }
    if (field === 'state' || field === 'county') {
      return [field, source[field] ?? ''];
    }
    return [field, normalizeConflictValue(source[field])];
  }));
}

function conflictValuesEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function changedTicketFieldsSinceBase(
  baseSnapshot: TicketConflictSnapshot,
  currentTicket: Ticket,
): string[] {
  const currentSnapshot = createTicketConflictSnapshot(currentTicket);
  return TICKET_CONFLICT_FIELDS.filter((field) =>
    !conflictValuesEqual(baseSnapshot[field], currentSnapshot[field]),
  );
}

function describeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([key, nested]) => key !== 'id' && key !== '_clientId' && nested !== null && nested !== undefined && nested !== '')
      .map(([key, nested]) => `${key}: ${typeof nested === 'object' ? JSON.stringify(nested) : String(nested)}`)
      .join(', ');
  }
  return String(value);
}

/**
 * A plain-text copy of the draft's fields that differ from the version it was
 * based on, for the reload-required banner: the user copies it, reloads, and
 * re-enters what is missing. Child sections are listed whole when changed.
 */
export function describeUnsavedTicketEdits(
  payload: UpdateTicketData,
  baseSnapshot: TicketConflictSnapshot | undefined,
): string {
  const source = payload as Record<string, unknown>;
  const lines: string[] = [];
  // The conflict fields first (unchanged), then any other field the payload
  // writes, such as the Flag button's bill_to_needs_review (#1041).
  const known = new Set<string>(TICKET_CONFLICT_FIELDS);
  const fields = [
    ...TICKET_CONFLICT_FIELDS,
    ...Object.keys(source).filter((field) => !known.has(field) && !NON_FIELD_PAYLOAD_KEYS.has(field)),
  ];
  for (const field of fields) {
    if (field === 'updated_at' || field === 'status' || field === 'is_locked') continue;
    if (!(field in source)) continue;
    const value = source[field];
    if (baseSnapshot && conflictValuesEqual(baseSnapshot[field], normalizeConflictValue(value))) continue;
    if (Array.isArray(value)) {
      lines.push(`${field}:`);
      if (value.length === 0) lines.push('  (none)');
      for (const row of value) lines.push(`  - ${describeValue(row)}`);
    } else {
      lines.push(`${field}: ${describeValue(value)}`);
    }
  }
  return lines.length > 0 ? lines.join('\n') : 'No unsaved edits.';
}

/** Whether the conflict snapshot holds `field` (so a server change to it is known). */
export function isTicketConflictField(field: string): boolean {
  return (TICKET_CONFLICT_FIELDS as readonly string[]).includes(field);
}

/** Payload keys that pin or acknowledge a PUT rather than write a field. */
const NON_FIELD_PAYLOAD_KEYS = new Set([
  'base_revision',
  'base_updated_at',
  'qbo_edit_acknowledged',
  'qbo_apply_shared_to_group',
  'updated_at',
  'status',
  'is_locked',
]);

/**
 * The fields `payload` changed from `base`, compared as
 * describeUnsavedTicketEdits compares them. Every field the payload writes
 * counts, not only TICKET_CONFLICT_FIELDS (the Flag button's
 * `bill_to_needs_review` is outside it). `base` is the payload the form
 * held for the base version; with no base every field counts.
 */
export function editedTicketFieldsSinceBase(
  payload: UpdateTicketData,
  base: Record<string, unknown> | undefined,
): string[] {
  const source = payload as Record<string, unknown>;
  const known = new Set<string>(TICKET_CONFLICT_FIELDS);
  const fields = [
    ...TICKET_CONFLICT_FIELDS.filter((field) => field in source),
    ...Object.keys(source).filter((field) => !known.has(field)),
  ];
  return fields.filter((field) => !NON_FIELD_PAYLOAD_KEYS.has(field) &&
    (!base || !conflictValuesEqual(normalizeConflictValue(base[field]), normalizeConflictValue(source[field]))));
}

/** A ticket PUT's optimistic-concurrency token (#967). */
export type TicketVersionToken = Pick<UpdateTicketData, 'base_revision' | 'base_updated_at'>;

/**
 * The token for an edit of `ticket`: its integer revision. Only a ticket
 * from a backend that predates the revision falls back to updated_at.
 */
export function ticketVersionToken(ticket: Pick<Ticket, 'revision' | 'updated_at'> | null | undefined): TicketVersionToken {
  if (typeof ticket?.revision === 'number') return { base_revision: ticket.revision };
  return ticket?.updated_at ? { base_updated_at: ticket.updated_at } : {};
}

/** The token a stored payload carries (a queued payload may predate base_revision). */
export function payloadVersionToken(payload: UpdateTicketData): TicketVersionToken {
  if (typeof payload.base_revision === 'number') return { base_revision: payload.base_revision };
  return payload.base_updated_at ? { base_updated_at: payload.base_updated_at } : {};
}

/** `payload` pinned to exactly `token` (any earlier token removed). */
export function withVersionToken(payload: UpdateTicketData, token: TicketVersionToken): UpdateTicketData {
  const { base_revision: _revision, base_updated_at: _updatedAt, ...rest } = payload;
  return { ...rest, ...token };
}

export function rebaseTicketSaveForSystemChanges(
  payload: UpdateTicketData,
  baseSnapshot: TicketConflictSnapshot | undefined,
  currentTicket: Ticket,
): UpdateTicketData | null {
  const token = ticketVersionToken(currentTicket);
  if (!baseSnapshot || Object.keys(token).length === 0) return null;
  const changedFields = changedTicketFieldsSinceBase(baseSnapshot, currentTicket);
  if (changedFields.some((field) => !SYSTEM_OWNED_TICKET_REBASE_FIELD_SET.has(field))) {
    return null;
  }

  const rebased = withVersionToken(payload, token);
  // Preserve a deliberate local state/county edit. Otherwise carry the
  // geocoder's current value forward so the retry cannot erase its own rebase.
  for (const field of ['state', 'county'] as const) {
    if (conflictValuesEqual(normalizeConflictValue(payload[field]), baseSnapshot[field])) {
      rebased[field] = currentTicket[field] ?? '';
    }
  }
  return rebased;
}

export function isTicketVersionConflict(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  // 503 save_outcome_unknown: the server could not confirm whether the save
  // committed. Handled exactly like a conflict: reload, then rebase (only if
  // nothing but system fields moved) or show the conflict. Never a blind
  // retry of the same payload.
  if (isSaveOutcomeUnknown(error)) return true;
  const apiError = error as { status?: number; data?: { error?: string } };
  return apiError.status === 409 && apiError.data?.error === 'ticket_version_conflict';
}

/**
 * Fired on `window` after a queued save is successfully replayed, so a mounted
 * ticket form can flip its SaveStatusPill back to "saved" without polling.
 */
export const OFFLINE_SAVE_REPLAYED_EVENT = 'fieldtickets:offline-save-replayed';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    let settled = false;
    const failOpen = (error: Error | DOMException) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      reject(error);
    };
    // A stalled browser request must not hold the per-ticket save lock forever.
    const timeout = setTimeout(() => {
      failOpen(new Error('IndexedDB open timed out; using the offline save mirror'));
    }, DB_OPEN_TIMEOUT_MS);
    req.onblocked = () => failOpen(new Error(
      'IndexedDB upgrade blocked by another tab; close older tabs to retry',
    ));
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion;
      const hadQueue = db.objectStoreNames.contains(STORE);
      const hadQuarantine = oldVersion >= 2 && db.objectStoreNames.contains(QUARANTINE_STORE);
      let rows: QueuedTicketSave[] = [];
      let quarantined: QuarantinedTicketSave[] = [];

      let pending = Number(hadQueue) + Number(hadQuarantine);
      const migrate = () => {
        if (hadQueue) db.deleteObjectStore(STORE);
        const queue = db.createObjectStore(STORE, { keyPath: ['owner', 'ticketId', 'tabId', 'sequence'] });
        if (hadQuarantine) db.deleteObjectStore(QUARANTINE_STORE);
        db.createObjectStore(QUARANTINE_STORE, { keyPath: 'id' });
        const quarantine = req.transaction!.objectStore(QUARANTINE_STORE);
        db.createObjectStore(FAILURE_STORE, { keyPath: ['owner', 'ticketId', 'tabId', 'sequence'] });
        db.createObjectStore(RECEIPT_STORE, { keyPath: ['owner', 'ticketId', 'tabId', 'sequence', 'kind'] });
        for (const row of rows) {
          if (!row.owner || (oldVersion < 3 && row.failure?.code === 'legacy_manual_retry')) {
            quarantine.add(quarantineEntry(row));
          } else queue.add(legacyEntry(row, 'idb'));
        }
        for (const key of syncMirrorKeys()) {
          const mirror = readLegacySyncMirror(key);
          const attributed = readStored<QueuedTicketSave>(key);
          if (mirror && (!attributed?.owner || oldVersion < 3)) {
            quarantine.add(quarantineEntry(mirror.record));
          } else if (attributed?.owner && attributed.payload) {
            const entry = legacyEntry(attributed, 'mirror');
            queue.add(entry);
          }
        }
        for (const row of quarantined) quarantine.add(quarantineEntry(row));
      };
      const read = <T>(name: string, assign: (entries: T[]) => void) => {
        const request = req.transaction!.objectStore(name).getAll();
        request.onsuccess = () => {
          assign(request.result);
          if (--pending === 0) migrate();
        };
      };
      if (hadQueue) read<QueuedTicketSave>(STORE, (entries) => { rows = entries; });
      if (hadQuarantine) read<QuarantinedTicketSave>(QUARANTINE_STORE, (entries) => { quarantined = entries; });

      if (!pending) migrate();
    };
    req.onsuccess = () => {
      const db = req.result;
      // Old mutable mirrors remain untouched. Removing one after a comparison
      // could delete an old-code tab's concurrent write. Readers use the stable
      // synthetic identity below, so an acknowledged copy cannot resurrect.
      db.onversionchange = () => {
        db.close();
        if (dbPromise === opening) dbPromise = null;
      };
      // Open requests cannot be cancelled. An abandoned request can still
      // succeed after a block/timeout; never cache or leak that connection.
      if (settled) {
        db.close();
        return;
      }
      settled = true;
      clearTimeout(timeout);
      resolve(db);
    };
    req.onerror = () => failOpen(req.error ?? new Error('IndexedDB open failed'));
  });
  dbPromise = opening;
  // If open fails, drop the cached promise so a later call can retry.
  opening.catch(() => {
    if (dbPromise === opening) dbPromise = null;
  });
  return opening;
}

function runRequest<T>(
  mode: IDBTransactionMode,
  makeRequest: (store: IDBObjectStore, tx: IDBTransaction) => IDBRequest,
  stores: string[] = [STORE],
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(stores, mode);
        const store = tx.objectStore(stores[0]);
        const req = makeRequest(store, tx);
        let result: T;
        req.onsuccess = () => {
          result = req.result as T;
        };
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
        // Settle on TRANSACTION completion, not request success: a transaction
        // that aborts after the request succeeded (e.g. quota exceeded at
        // commit) must reject, otherwise the promise — and the per-ticket save
        // lock awaiting it — would hang forever. tx.oncomplete fires after
        // req.onsuccess, so `result` is populated by then.
        tx.oncomplete = () => resolve(result);
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
        tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
      }),
  );
}

const OWNER_STORAGE_KEY = 'alturex-tickets-offline-owner';
let memoryOnlyOwner = false;
let memoryOwner: string | null = null;
interface MemoryTicketSnapshot { record: QueuedTicketSave; memoryOnly: boolean }
// Old range receipts are read as exact acknowledgements, never as sequence cutoffs.
interface EntryReceipt extends TicketSaveAdmissionIdentity { updatedAt: number; kind: 'through' | 'exact' }
const memorySnapshots = new Map<string, MemoryTicketSnapshot>();
const memoryFailures = new Map<string, QueuedTicketSaveFailure>();
const memoryReceipts = new Map<string, EntryReceipt>();
const pendingSnapshots = new Map<Promise<boolean>, string>();
export const MEMORY_TICKET_SAVES_EVENT = 'ft:memory-ticket-saves';
export const OFFLINE_SAVE_FAILED_EVENT = 'ft:offline-save-failed';

export function ticketSaveEntryId(entry: Pick<QueuedTicketSave, 'owner' | 'ticketId' | 'tabId' | 'sequence'>): string {
  return [entry.owner, entry.ticketId, entry.tabId, String(entry.sequence)].map(encodeURIComponent).join(':');
}
const entryKey = (entry: QueuedTicketSave | EntryReceipt) => [entry.owner, entry.ticketId, entry.tabId, entry.sequence];
const mirrorKey = (entry: QueuedTicketSave | EntryReceipt) => ENTRY_PREFIX + ticketSaveEntryId(entry);
function readStored<T>(key: string): T | undefined {
  try { return JSON.parse(localStorage.getItem(key) ?? 'null') ?? undefined; } catch { return undefined; }
}
function keysWithPrefix(prefix: string): string[] {
  try { return Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).filter((key): key is string => Boolean(key?.startsWith(prefix))); }
  catch { return []; }
}
function legacyEntry(row: QueuedTicketSave, source: string): QueuedTicketSave {
  // Include the original serialized snapshot in the synthetic identity. Old
  // mirror keys may still be written by old code; each distinct value survives.
  const identity = encodeURIComponent(JSON.stringify([row.payload, row.baseSnapshot, row.updatedAt, row.sequence]));
  return { ...row, tabId: `legacy:${source}:${identity}`, sequence: row.sequence ?? 0, baseRevision: row.payload.base_revision };
}
/** Unattributed rows get the same immutable identity shape, with no auth owner. */
function quarantineEntry(row: Pick<QuarantinedTicketSave, 'ticketId' | 'payload' | 'baseSnapshot' | 'updatedAt'> & Partial<QuarantinedTicketSave>): QuarantinedTicketSave {
  const tabId = row.tabId?.startsWith('legacy:quarantine:') ? row.tabId : `legacy:quarantine:${crypto.randomUUID()}`;
  const sequence = row.sequence ?? 0;
  return { id: ticketSaveEntryId({ owner: 'unattributed', ticketId: row.ticketId, tabId, sequence }),
    ticketId: row.ticketId, tabId, sequence, payload: row.payload, baseSnapshot: row.baseSnapshot,
    baseRevision: row.payload.base_revision, updatedAt: row.updatedAt };
}
function receipts(): EntryReceipt[] {
  return [...keysWithPrefix(RECEIPT_PREFIX).map((key) => readStored<EntryReceipt>(key)).filter((row): row is EntryReceipt => Boolean(row)), ...memoryReceipts.values()];
}
function delivered(entry: QueuedTicketSave, all = receipts()): boolean {
  return all.some((receipt) => receipt.owner === entry.owner && receipt.ticketId === entry.ticketId && receipt.tabId === entry.tabId &&
    receipt.sequence === entry.sequence);
}
function sameEntry(a: QueuedTicketSave | undefined, b: QueuedTicketSave): boolean {
  return Boolean(a && ticketSaveEntryId(a) === ticketSaveEntryId(b) && conflictValuesEqual(a.payload, b.payload) && conflictValuesEqual(a.baseSnapshot, b.baseSnapshot) && conflictValuesEqual(a.incorporatedAdmissions, b.incorporatedAdmissions) && a.baseRevision === b.baseRevision && a.updatedAt === b.updatedAt);
}
function notifyMemorySnapshots(): void { window.dispatchEvent(new Event(MEMORY_TICKET_SAVES_EVENT)); }

export function initializeOfflineQueueTab(): Promise<void> {
  return initializeTicketQueueTab(async () => {
    const db = await openDb();
    const rows = await new Promise<(QueuedTicketSave | EntryReceipt)[]>((resolve, reject) => {
      const tx = db.transaction([STORE, RECEIPT_STORE], 'readonly');
      const all: (QueuedTicketSave | EntryReceipt)[] = [];
      for (const name of [STORE, RECEIPT_STORE]) tx.objectStore(name).getAll().onsuccess = (event) => all.push(...(event.target as IDBRequest).result);
      tx.oncomplete = () => resolve(all);
      tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('Snapshot identities could not be loaded'));
    });
    return [...rows, ...[...keysWithPrefix(ENTRY_PREFIX), ...keysWithPrefix(RECEIPT_PREFIX)].map((key) => readStored<QueuedTicketSave | EntryReceipt>(key)).filter((row): row is QueuedTicketSave | EntryReceipt => Boolean(row))];
  });
}
export { isTicketQueueTabReady };

/** Synchronous per-tab admission, after this tab's entries have loaded. */
export function createTicketSaveSnapshot(owner: string, ticketId: string, payload: UpdateTicketData,
  baseSnapshot?: TicketConflictSnapshot, failure?: QueuedTicketSaveFailure, updatedAt = Date.now(),
  incorporatedAdmissions: readonly TicketSaveAdmissionIdentity[] = []): QueuedTicketSave {
  const incorporated = incorporatedAdmissions.filter((entry) => entry.owner === owner && entry.ticketId === ticketId)
    .map(({ owner, ticketId, tabId, sequence }) => ({ owner, ticketId, tabId, sequence }));
  return { owner, ticketId, payload: structuredClone(payload), baseSnapshot: baseSnapshot && structuredClone(baseSnapshot), failure, updatedAt,
    tabId: ticketQueueTabId(), sequence: nextTicketQueueSequence(), baseRevision: payload.base_revision,
    ...(incorporated.length ? { incorporatedAdmissions: incorporated } : {}) };
}
function admittedRecord(owner: string, ticketId: string, payload: UpdateTicketData, baseSnapshot?: TicketConflictSnapshot,
  failure?: QueuedTicketSaveFailure, updatedAt = Date.now(), admission?: QueuedTicketSave): QueuedTicketSave {
  return admission ?? createTicketSaveSnapshot(owner, ticketId, payload, baseSnapshot, failure, updatedAt);
}

/** Payloads and receipts are append-only. A duplicate key must confirm THIS entry. */
async function appendRecord<T extends QueuedTicketSave | EntryReceipt>(name: string, record: T, allowed: () => boolean = () => true): Promise<boolean> {
  const db = await openDb();
  if (!allowed()) throw new StaleSessionError();
  return new Promise<boolean>((resolve, reject) => {
    const tx = db.transaction(name === RECEIPT_STORE ? RECEIPT_STORE : [name, RECEIPT_STORE], 'readwrite');
    const store = tx.objectStore(name);
    let written = false;
    const receiptRead = tx.objectStore(RECEIPT_STORE).getAll();
    receiptRead.onsuccess = () => {
      if (!allowed() || (name === STORE && delivered(record as QueuedTicketSave, [...receiptRead.result, ...receipts()]))) return;
      const request = store.get(name === RECEIPT_STORE ? [...entryKey(record), (record as EntryReceipt).kind] : entryKey(record));
      request.onsuccess = () => {
        if (!allowed() || (name === STORE && delivered(record as QueuedTicketSave, [...receiptRead.result, ...receipts()]))) return;
        const existing = request.result as T | undefined;
        if (!existing) { store.add(record); written = true; }
        else written = name === STORE ? sameEntry(existing as QueuedTicketSave, record as QueuedTicketSave) : conflictValuesEqual(existing, record);
      };
    };
    tx.oncomplete = () => resolve(written);
    tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('Offline snapshot append failed'));
  });
}
function appendMirror(record: QueuedTicketSave): boolean {
  if (delivered(record)) return false;
  const key = mirrorKey(record);
  const existing = readStored<QueuedTicketSave>(key);
  if (existing) return sameEntry(existing, record);
  writeTicketStorage(key, JSON.stringify(record));
  return sameEntry(readStored<QueuedTicketSave>(key), record);
}
async function putQueuedRecord(record: QueuedTicketSave): Promise<boolean> {
  await enforceQueueRetention();
  let stored: boolean;
  try { stored = await appendRecord(STORE, record); } catch (error) {
    if (!isStorageQuotaError(error)) throw error;
    await enforceQueueRetention();
    stored = await appendRecord(STORE, record);
  }
  if (stored) { memorySnapshots.delete(ticketSaveEntryId(record)); notifyMemorySnapshots(); }
  return stored;
}
export function __resetMemoryTicketSavesForTest(): void {
  memorySnapshots.clear(); memoryFailures.clear(); memoryReceipts.clear();
}

export function memoryOnlyTicketSaveText(owner: string | null): string {
  pruneMirrors();
  if (!owner || owner !== memoryOwner) return '';
  return [...memorySnapshots.values()].filter((entry) => entry.memoryOnly && entry.record.owner === owner)
    .map(({ record }) => `Ticket ${record.ticketId}\n${describeUnsavedTicketEdits(record.payload, record.baseSnapshot)}`).join('\n\n');
}

function isQueueOwner(owner: string | null): owner is string {
  if (!owner || memoryOwner !== owner) return false;
  try {
    const stored = localStorage.getItem(OWNER_STORAGE_KEY);
    return stored === owner || (stored === null && memoryOnlyOwner);
  } catch {
    return true;
  }
}

/** Session changes affect visibility only; owner-keyed edits never follow a login. */
export function syncOfflineQueueOwner(owner: string | null): void {
  memoryOwner = owner;
  if (!owner) return;
  void initializeOfflineQueueTab();
  void enforceQueueRetention().catch(() => undefined);
  try {
    writeTicketStorage(OWNER_STORAGE_KEY, owner);
    // r5's purge marker is obsolete. Never resume a destructive login purge.
    localStorage.removeItem('alturex-tickets-offline-purge');
    memoryOnlyOwner = false;
  } catch {
    memoryOnlyOwner = true;
  }
}

function expired(entry: { updatedAt: number }): boolean {
  return Date.now() - entry.updatedAt >= OFFLINE_SAVE_MAX_AGE_MS;
}

function pruneMirrors(): void {
  pruneExpiredTicketStorage();
  for (const [key, entry] of memorySnapshots) if (expired(entry.record)) memorySnapshots.delete(key);
  for (const [key, receipt] of memoryReceipts) if (expired(receipt)) memoryReceipts.delete(key);
}
async function enforceQueueRetention(): Promise<void> {
  pruneMirrors();
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE, QUARANTINE_STORE, FAILURE_STORE, RECEIPT_STORE], 'readwrite');
    for (const name of [STORE, QUARANTINE_STORE, FAILURE_STORE, RECEIPT_STORE]) {
      const store = tx.objectStore(name);
      store.getAll().onsuccess = (event) => {
        for (const row of (event.target as IDBRequest).result) if (expired(row)) store.delete(name === QUARANTINE_STORE ? row.id : name === RECEIPT_STORE ? [...entryKey(row), row.kind] : entryKey(row));
      };
    }
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('Offline save retention failed'));
  });
}
async function retainQueuedRecord(record: QueuedTicketSave): Promise<boolean> {
  pruneMirrors();
  const key = ticketSaveEntryId(record);
  if (delivered(record)) return false;
  const entry = memorySnapshots.get(key) ?? { record, memoryOnly: false };
  if (!sameEntry(entry.record, record)) return false;
  memorySnapshots.set(key, entry);
  let durable = false;
  try { durable = appendMirror(record); } catch { /* Try IDB next. */ }
  try { durable = await putQueuedRecord(record) || durable; } catch { /* Keep precisely this entry in this tab. */ }
  if (memorySnapshots.get(key) === entry) {
    if (durable) memorySnapshots.delete(key);
    else entry.memoryOnly = true;
    notifyMemorySnapshots();
  }
  return durable;
}
export function snapshotTicketSave(owner: string | null, ticketId: string, payload: UpdateTicketData,
  baseSnapshot?: TicketConflictSnapshot, failure?: QueuedTicketSaveFailure, updatedAt = Date.now(), admission?: QueuedTicketSave): Promise<boolean> {
  if (!owner) return Promise.resolve(false);
  const write = () => retainQueuedRecord(admittedRecord(owner, ticketId, payload, baseSnapshot, failure, updatedAt, admission));
  const pending = isTicketQueueTabReady() ? write() : initializeOfflineQueueTab().then(write);
  pendingSnapshots.set(pending, owner);
  void pending.then(() => pendingSnapshots.delete(pending), () => pendingSnapshots.delete(pending));
  return pending;
}

/** Include departing forms whose admitted snapshot is still awaiting storage. */
export async function settlePendingTicketSnapshots(owner: string | null): Promise<void> {
  while (owner) {
    const pending = [...pendingSnapshots].filter(([, author]) => author === owner).map(([promise]) => promise);
    if (!pending.length) return;
    await Promise.all(pending);
  }
}

/** Failure metadata has its own immutable key; the payload is never rewritten. */
export async function markQueuedTicketSaveFailed(snapshot: QueuedTicketSave, failure: QueuedTicketSaveFailure): Promise<boolean> {
  if (delivered(snapshot)) return false;
  const key = ticketSaveEntryId(snapshot);
  memoryFailures.set(key, memoryFailures.get(key) ?? failure);
  const marker = { ...snapshot, payload: snapshot.payload, failure, updatedAt: snapshot.updatedAt };
  try {
    if (!readStored(FAILURE_PREFIX + key)) writeTicketStorage(FAILURE_PREFIX + key, JSON.stringify(marker));
  } catch { /* The memory marker still blocks automatic replay. */ }
  try { await appendRecord(FAILURE_STORE, marker); } catch { /* Keep local recovery metadata. */ }
  if (isQueueOwner(snapshot.owner)) window.dispatchEvent(new CustomEvent(OFFLINE_SAVE_FAILED_EVENT, { detail: { ...snapshot, failure } }));
  return true;
}

export async function getQuarantinedTicketSaves(ticketId: string): Promise<QuarantinedTicketSave[]> {
  await enforceQueueRetention();
  const rows = await runRequest<QuarantinedTicketSave[]>('readonly', (store) => store.getAll(), [QUARANTINE_STORE]);
  return rows.filter((row) => row.ticketId === ticketId);
}

/** Caller re-checks a successful ticket GET and session before this explicit action. */
export async function restoreQuarantinedTicketSave(owner: string, id: string, ticketId: string): Promise<QueuedTicketSave | undefined> {
  if (!isQueueOwner(owner)) return undefined;
  await initializeOfflineQueueTab();
  if (!isQueueOwner(owner)) return undefined;
  pruneMirrors();
  const epoch = getAuthEpoch();
  await enforceQueueRetention();
  const entry = await runRequest<QuarantinedTicketSave | undefined>('readonly', (store) => store.get(id), [QUARANTINE_STORE]);
  if (!entry || entry.ticketId !== ticketId || !isQueueOwner(owner) || !isCurrentAuthEpoch(epoch)) return undefined;
  const restored: QueuedTicketSave = {
    ...createTicketSaveSnapshot(owner, entry.ticketId, entry.payload, entry.baseSnapshot),
    failure: {
      failedAt: Date.now(), code: 'legacy_manual_retry',
      message: 'Recovered unattributed edit. Review it and save manually as your account.',
    },
  };
  await retainQueuedRecord(restored);
  return isQueueOwner(owner) && isCurrentAuthEpoch(epoch) ? restored : undefined;
}

/** Append a distinct admission for the signed-in owner. */
export async function enqueueTicketSave(
  owner: string | null,
  ticketId: string,
  payload: UpdateTicketData,
  baseSnapshot?: TicketConflictSnapshot,
  failure?: QueuedTicketSaveFailure, admission?: QueuedTicketSave,
): Promise<boolean> {
  if (!isQueueOwner(owner)) return false;
  const epoch = getAuthEpoch();
  try {
    const record = isTicketQueueTabReady() ? admittedRecord(owner, ticketId, payload, baseSnapshot, failure, Date.now(), admission) : undefined;
    await initializeOfflineQueueTab();
    const initializedRecord = record ?? admittedRecord(owner, ticketId, payload, baseSnapshot, failure, Date.now(), admission);
    const stored = await retainQueuedRecord(initializedRecord);
    return stored && isQueueOwner(owner) && isCurrentAuthEpoch(epoch);
  } catch (error) {
    if (!isQueueOwner(owner) || !isCurrentAuthEpoch(epoch)) return false;
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Synchronous last-ditch mirror (localStorage).
//
// IndexedDB writes are ASYNC and are NOT guaranteed to flush before mobile
// Safari freezes then DISCARDS a backgrounded tab (Page Lifecycle): a tech who
// makes a last edit and immediately backgrounds the app can lose it if iOS
// evicts the tab before the IDB transaction commits. A localStorage write is
// SYNCHRONOUS and survives that freeze→evict window, so the hide/unmount
// handlers ALSO mirror the dirty snapshot here. Readers union the two
// stores by immutable identity, retaining every undelivered admission.
// ---------------------------------------------------------------------------
const SYNC_MIRROR_PREFIX = 'alturex-tickets-offline:save:';

/**
 * Best-effort SYNCHRONOUS persistence of a dirty snapshot to localStorage,
 * using the same immutable identity as IDB. Used
 * alongside (not instead of) `enqueueTicketSave` in the page-hide / unmount
 * handlers, where the async IDB commit may not land before a tab discard.
 */
export function persistTicketSaveSync(
  owner: string | null,
  ticketId: string,
  payload: UpdateTicketData,
  baseSnapshot?: TicketConflictSnapshot,
  failure?: QueuedTicketSaveFailure, admission?: QueuedTicketSave,
): boolean {
  if (!isQueueOwner(owner) || typeof localStorage === 'undefined') return false;
  try {
    const record = admittedRecord(owner, ticketId, payload, baseSnapshot, failure, Date.now(), admission);
    const stored = appendMirror(record);
    if (stored) { memorySnapshots.delete(ticketSaveEntryId(record)); notifyMemorySnapshots(); }
    return stored;
  } catch {
    // Quota exceeded / private mode — best effort only; the async IDB enqueue
    // remains the primary durable path.
    return false;
  }
}

function readLegacySyncMirror(key: string): { record: Omit<QueuedTicketSave, 'owner' | 'sequence' | 'tabId'> } | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const record = JSON.parse(raw) as QueuedTicketSave;
    const wasAttributedLegacy = record.owner && record.failure?.code === 'legacy_manual_retry';
    if ((record.owner && !wasAttributedLegacy) || typeof record.ticketId !== 'string' || typeof record.updatedAt !== 'number' ||
      !record.payload || typeof record.payload !== 'object') return undefined;
    if (key !== `${SYNC_MIRROR_PREFIX}${record.ticketId}` &&
      key !== `${SYNC_MIRROR_PREFIX}${encodeURIComponent(record.ticketId)}` &&
      !(wasAttributedLegacy && key === syncMirrorKey(record.owner, record.ticketId))) return undefined;
    return { record: { ticketId: record.ticketId, payload: record.payload, baseSnapshot: record.baseSnapshot, updatedAt: record.updatedAt } };
  } catch {
    return undefined;
  }
}

function syncMirrorOwnerPrefix(owner: string): string {
  return `${SYNC_MIRROR_PREFIX}${encodeURIComponent(owner)}:`;
}

function syncMirrorKey(owner: string, ticketId: string): string {
  return `${syncMirrorOwnerPrefix(owner)}${encodeURIComponent(ticketId)}`;
}

function syncMirrorKeys(): string[] {
  if (typeof localStorage === 'undefined') return [];
  const ids: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(SYNC_MIRROR_PREFIX)) {
        ids.push(key);
      }
    }
  } catch {
    return ids;
  }
  return ids;
}

/** Hydration chooses this tab's newest admission; conflicting variants take priority. */
export async function getQueuedTicketSave(owner: string | null, ticketId: string): Promise<QueuedTicketSave | undefined> {
  const rows = (await getAllQueuedTicketSaves(owner)).filter((row) => row.ticketId === ticketId);
  const own = rows.filter((row) => row.tabId === ticketQueueTabId());
  if (!own.length && rows.some((row) => !row.tabId.startsWith('legacy:'))) return undefined;
  return (own.length ? own : rows).sort((a, b) => a.tabId === b.tabId ? b.sequence - a.sequence : b.updatedAt - a.updatedAt)[0];
}
export async function getAllQueuedTicketSaves(owner: string | null): Promise<QueuedTicketSave[]> {
  if (!isQueueOwner(owner)) return [];
  await initializeOfflineQueueTab();
  pruneMirrors();
  const epoch = getAuthEpoch();
  let idb: QueuedTicketSave[] = [], failures: QueuedTicketSave[] = [], acknowledged: EntryReceipt[] = [];
  try {
    await enforceQueueRetention();
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE, FAILURE_STORE, RECEIPT_STORE], 'readonly');
      tx.objectStore(STORE).getAll().onsuccess = (event) => { idb = (event.target as IDBRequest).result; };
      tx.objectStore(FAILURE_STORE).getAll().onsuccess = (event) => { failures = (event.target as IDBRequest).result; };
      tx.objectStore(RECEIPT_STORE).getAll().onsuccess = (event) => { acknowledged = (event.target as IDBRequest).result; };
      tx.oncomplete = () => resolve();
      tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('Offline journal read failed'));
    });
  } catch { /* Mirrors and page-lifetime entries remain readable. */ }
  if (!isQueueOwner(owner) || !isCurrentAuthEpoch(epoch)) return [];
  const byId = new Map<string, QueuedTicketSave>();
  const allReceipts = [...acknowledged, ...receipts()];
  const failureById = new Map(failures.map((entry) => [ticketSaveEntryId(entry), entry.failure]));
  for (const key of keysWithPrefix(FAILURE_PREFIX)) {
    const entry = readStored<QueuedTicketSave>(key);
    if (entry) failureById.set(ticketSaveEntryId(entry), entry.failure);
  }
  for (const [key, failure] of memoryFailures) failureById.set(key, failure);
  const legacy = syncMirrorKeys().map((key) => readStored<QueuedTicketSave>(key))
    .filter((row): row is QueuedTicketSave => Boolean(row?.owner && row.payload && row.failure?.code !== 'legacy_manual_retry'))
    .map((row) => legacyEntry(row, 'mirror'));
  const mirrors = keysWithPrefix(ENTRY_PREFIX).map((key) => readStored<QueuedTicketSave>(key)).filter((row): row is QueuedTicketSave => Boolean(row));
  for (const entry of [...idb, ...legacy, ...mirrors, ...[...memorySnapshots.values()].map((entry) => entry.record)]) {
    if (entry.owner !== owner || expired(entry) || delivered(entry, allReceipts)) continue;
    observeOwnTicketQueueEntry(entry);
    byId.set(ticketSaveEntryId(entry), { ...entry, failure: failureById.get(ticketSaveEntryId(entry)) ?? entry.failure });
  }
  return [...byId.values()].sort(compareQueuedEntries);
}
function compareQueuedEntries(a: QueuedTicketSave, b: QueuedTicketSave): number {
  return (a.baseRevision ?? 0) - (b.baseRevision ?? 0) || a.updatedAt - b.updatedAt || a.tabId.localeCompare(b.tabId) || a.sequence - b.sequence;
}

/** Acknowledge only the dispatched identity and its explicitly incorporated admissions. */
export async function deleteTicketSave(snapshot: TicketSaveAdmissionIdentity, incorporated: readonly TicketSaveAdmissionIdentity[] = []): Promise<void> {
  if (!isQueueOwner(snapshot.owner)) return;
  if (!isTicketQueueTabReady()) await initializeOfflineQueueTab();
  if (!isQueueOwner(snapshot.owner)) return;
  const epoch = getAuthEpoch();
  const acknowledged = new Map([snapshot, ...incorporated]
    .filter((entry) => entry.owner === snapshot.owner && entry.ticketId === snapshot.ticketId)
    .map(({ owner, ticketId, tabId, sequence }) => [ticketSaveEntryId({ owner, ticketId, tabId, sequence }),
      { owner, ticketId, tabId, sequence, updatedAt: Date.now(), kind: 'exact' as const }]));
  for (const [id, receipt] of acknowledged) {
    const receiptId = id + ':exact';
    memoryReceipts.set(receiptId, memoryReceipts.get(receiptId) ?? receipt);
    try { if (!readStored(RECEIPT_PREFIX + receiptId)) writeTicketStorage(RECEIPT_PREFIX + receiptId, JSON.stringify(receipt)); } catch { /* IDB persists delivery too. */ }
  }
  const matches = (row: QueuedTicketSave) => acknowledged.has(ticketSaveEntryId(row));
  for (const key of keysWithPrefix(ENTRY_PREFIX)) {
    const row = readStored<QueuedTicketSave>(key);
    if (row && matches(row)) { try { localStorage.removeItem(key); } catch { /* The receipt hides the delivered key. */ } } // Immutable key: another tab can never replace its contents.
  }
  for (const [key, entry] of memorySnapshots) if (matches(entry.record)) memorySnapshots.delete(key);
  notifyMemorySnapshots();
  try {
    await Promise.all([...acknowledged.values()].map((receipt) => appendRecord(RECEIPT_STORE, receipt, () => isQueueOwner(snapshot.owner) && isCurrentAuthEpoch(epoch))));
    const db = await openDb();
    if (!isQueueOwner(snapshot.owner) || !isCurrentAuthEpoch(epoch)) return;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      store.getAll().onsuccess = (event) => {
        if (!isQueueOwner(snapshot.owner) || !isCurrentAuthEpoch(epoch)) return;
        for (const row of (event.target as IDBRequest).result as QueuedTicketSave[]) if (matches(row)) store.delete(entryKey(row));
      };
      tx.oncomplete = () => resolve();
      tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('Offline journal cleanup failed'));
    });
  } catch (error) {
    if (!isQueueOwner(snapshot.owner) || !isCurrentAuthEpoch(epoch)) return;
    throw error;
  }
}

/** Explicit owner discard, used only after the recovery UI captures the entries. */
export async function discardTicketSave(entry: QueuedTicketSave): Promise<void> { await deleteTicketSave(entry); }

/**
 * Classify a save failure as offline/network (queue + retry) vs. a real
 * server/validation error (surface to the user, do NOT queue).
 *
 * Shapes seen from the shared API client (packages/shared/src/api/client.ts):
 *  - raw fetch network failure → `TypeError` ("Failed to fetch")
 *  - request timeout (AbortError) → normalized to `{ status: 0 }`
 *  - caller-cancelled request → raw `AbortError` (NOT offline)
 *  - HTTP error → `ApiError { status, message, data }` (status >= 400)
 * A real HTTP response (>= 400) means we reached the server, so it is NEVER
 * offline even if `navigator.onLine` momentarily reads false — with ONE
 * exception: the gateway/bounce codes 502/503/504 (see below), which for a
 * ticket autosave PUT can only come from the backend bouncing during a deploy.
 */
export function isOfflineError(err: unknown): boolean {
  if (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: unknown }).name === 'AbortError'
  ) {
    return false;
  }
  const status =
    typeof err === 'object' && err !== null ? (err as { status?: number }).status : undefined;
  // Gateway/bounce codes — 502 Bad Gateway, 503 Service Unavailable, 504
  // Gateway Timeout. For a ticket autosave PUT these can ONLY originate from the
  // nginx/gateway/backend layer, classically the backend container being
  // recreated during a zero-downtime deploy: the request never reached
  // application logic, so the edit was NOT rejected — it was just momentarily
  // unservable. Queue + retry it exactly like a network drop instead of toasting
  // a hard error and risking a silently dropped edit (the "Rusty" incident).
  // ...except the app's own 503 save_outcome_unknown: the request ran and its
  // outcome is unknown, which is a conflict to reconcile, not a bounce.
  if (isSaveOutcomeUnknown(err)) return false;
  if (status === 502 || status === 503 || status === 504) return true;
  // Any other real HTTP response (>= 400) means we reached the server and it
  // genuinely handled/rejected the request (validation, auth, conflict). That
  // must surface to the user — never queue it — even if `navigator.onLine`
  // momentarily reads false. A 500, notably, is the app itself throwing on the
  // request (it ran), distinct from a transient gateway bounce.
  if (typeof status === 'number' && status >= 400) return false;
  if (status === 0) return true;
  if (err instanceof TypeError) return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return false;
}

/**
 * Per-ticket serialization lock SHARED by the autosave form loop and the
 * offline-queue replay. Both writers run their PUT inside this lock (keyed by
 * ticketId), so a replay PUT can never overlap — or land after — a newer form
 * save and silently revert the row on the server. Whichever writer acquires
 * the lock first runs to completion before the other starts; combined with the
 * exact-entry re-read in `replayQueuedTicketSave` and server revision check,
 * no obsolete payload can silently overwrite a later server save. The lock
 * map entry is deleted once its chain drains,
 * so it never grows unbounded.
 */
const ticketSaveLocks = new Map<string, Promise<unknown>>();

/**
 * Proof of holding a ticket's save lock: `active` while the task runs. The
 * ticket PUT gate (ticketPut.ts) refuses to send without an active lease.
 */
export interface TicketSaveLease {
  readonly ticketId: string;
  readonly active: boolean;
}

export function withTicketSaveLock<T>(
  ticketId: string,
  task: (lease: TicketSaveLease) => Promise<T>,
): Promise<T> {
  const prior = ticketSaveLocks.get(ticketId);
  const lease = { ticketId, active: false };
  const runTask = (): Promise<T> => {
    lease.active = true;
    let result: Promise<T>;
    try {
      result = task(lease);
    } catch (err) {
      result = Promise.reject(err);
    }
    // Deactivate alongside, not in, the returned chain, so taking the lock
    // adds no extra turn before the next holder runs.
    const deactivate = () => {
      lease.active = false;
    };
    result.then(deactivate, deactivate);
    return result;
  };
  // Uncontended: run synchronously so an autosave isn't gratuitously delayed by
  // a microtask. Contended: chain after the prior holder no matter how it
  // settled — one writer's failure must never wedge the lock for the next.
  const run = prior ? prior.then(runTask, runTask) : runTask();
  const tail = run.then(
    () => {
      if (ticketSaveLocks.get(ticketId) === tail) ticketSaveLocks.delete(ticketId);
    },
    () => {
      if (ticketSaveLocks.get(ticketId) === tail) ticketSaveLocks.delete(ticketId);
    },
  );
  ticketSaveLocks.set(ticketId, tail);
  return run;
}

/**
 * Test-only: drop all in-memory per-ticket locks. Production never needs this —
 * a real save always settles (success/error/timeout) and releases its lock — but
 * a test that deliberately leaves a save unsettled would otherwise wedge the
 * shared lock for the next test that reuses the same ticketId.
 */
export function __resetTicketSaveLocksForTest(): void {
  ticketSaveLocks.clear();
}

export type ReplayOutcome = 'replayed' | 'kept-offline' | 'failed' | 'blocked' | 'skipped' | 'stale';

/** Error codes of the ticket PUT gate (ticketPut.ts), read here by code. */
const TICKET_PUT_BLOCKED = 'ticket_put_blocked';
const STALE_SESSION = 'stale_session';

function errorCode(err: unknown): string | undefined {
  return typeof err === 'object' && err !== null ? (err as { code?: string }).code : undefined;
}

/**
 * Replay one queued payload via the injected PUT, under the shared per-ticket
 * lock so it can never run concurrently with that ticket's form autosave.
 *
 * Re-read the exact admission after acquiring the lock: a concurrent form
 * acknowledgement may already have superseded this tab's older entry. Send
 * its original server revision, never rebase or substitute another payload.
 * A server conflict/rejection retains the immutable entry with a separate
 * failure marker; future drains leave it for explicit review/export/discard.
 */
/** The replay's PUT, run under the ticket's save lock (`lease`). */
export type TicketReplayPut = (
  ticketId: string,
  payload: UpdateTicketData,
  baseSnapshot: TicketConflictSnapshot | undefined,
  lease: TicketSaveLease,
  entry?: QueuedTicketSave,
) => Promise<void>;

export interface ReplayOptions {
  /**
   * A ticket in recovery (#967) is never replayed: its edits wait in the
   * recovery draft for the user's reload.
   */
  isBlocked?: (ticketId: string) => boolean;
}

export async function replayQueuedTicketSave(
  entry: QueuedTicketSave,
  put: TicketReplayPut,
  options: ReplayOptions = {},
): Promise<ReplayOutcome> {
  const epoch = getAuthEpoch();
  if (!isQueueOwner(entry.owner)) return 'stale';
  const sameSession = () => isQueueOwner(entry.owner) && isCurrentAuthEpoch(epoch);
  await initializeOfflineQueueTab();
  if (!sameSession()) return 'stale';
  return withDepartedTicketQueueTab(entry.tabId, 'blocked', () => withTicketSaveLock<ReplayOutcome>(entry.ticketId, async (lease) => {
    if (!sameSession()) return 'stale';
    const current = (await getAllQueuedTicketSaves(entry.owner)).find((row) => ticketSaveEntryId(row) === ticketSaveEntryId(entry));
    if (!sameSession()) return 'stale';
    // Cleared while we waited for the lock → a form save already persisted
    // newer truth. Do NOT re-PUT our stale snapshot over it.
    if (!current) return 'skipped';
    if (options.isBlocked?.(current.ticketId)) return 'blocked';
    // A reached-server rejection requires a user-visible correction/retry. Keep
    // it recoverable, but do not hammer the same payload on every focus event.
    if (current.failure) return 'blocked';
    try {
      await put(current.ticketId, current.payload, current.baseSnapshot, lease, current);
    } catch (err) {
      // Actor mismatch is a server refusal, even when another tab changed
      // the owner marker. Retain and block A's entry without touching B's.
      const data = (err as { data?: { code?: string; error?: string } })?.data;
      if (data?.code === 'actor_mismatch' || data?.error === 'actor_mismatch') {
        await markQueuedTicketSaveFailed(current, {
          failedAt: Date.now(), code: 'actor_mismatch', status: 409,
          message: 'The signed-in user changed. Sign in as the edit owner before retrying.',
        });
        return 'failed';
      }
      // The session changed under the PUT: touch nothing of this queue.
      if (!sameSession() || errorCode(err) === STALE_SESSION) return 'stale';
      // A recovery draft appeared while the replay waited: it holds the edits.
      if (errorCode(err) === TICKET_PUT_BLOCKED) return 'blocked';
      if (isOfflineError(err)) return 'kept-offline';
      const apiError = err as {
        status?: number;
        message?: string;
        data?: { code?: string; error?: string; message?: string };
      };
      await markQueuedTicketSaveFailed(current, {
        failedAt: Date.now(),
        message: apiError.data?.message || apiError.message || 'Ticket save was rejected',
        status: apiError.status,
        code: apiError.data?.code || apiError.data?.error,
      });
      console.error(
        '[offlineQueue] A queued ticket edit was rejected and retained for recovery:',
        { ticketId: current.ticketId, payload: current.payload, error: err },
      );
      return 'failed';
    }
    if (!sameSession()) return 'stale';
    await deleteTicketSave(current);
    if (!sameSession()) return 'stale';
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(OFFLINE_SAVE_REPLAYED_EVENT, { detail: { ticketId: current.ticketId } }),
      );
    }
    return 'replayed';
  }));
}

export interface ReplayAllResult {
  replayed: string[];
  failed: string[];
}

/**
 * Drain the whole queue. Stops early on the first still-offline result (the
 * connection is down — remaining entries will retry on the next pass) so we
 * don't hammer a dead network. Replayed and failed ticketIds are returned so
 * the caller can invalidate caches / surface a notice.
 */
export async function replayAllQueuedTicketSaves(
  owner: string | null,
  put: TicketReplayPut,
  options: ReplayOptions = {},
): Promise<ReplayAllResult> {
  const result: ReplayAllResult = { replayed: [], failed: [] };
  let entries: QueuedTicketSave[];
  try {
    entries = await getAllQueuedTicketSaves(owner);
  } catch {
    return result;
  }
  for (const entry of entries) {
    const outcome = await replayQueuedTicketSave(entry, put, options);
    if (outcome === 'replayed') result.replayed.push(entry.ticketId);
    else if (outcome === 'failed') result.failed.push(entry.ticketId);
    else if (outcome === 'blocked') continue;
    // 'skipped' = a form save flushed this ticket while we drained; the entry
    // is already gone, so just move on to the rest of the queue.
    else if (outcome === 'skipped') continue;
    else break; // 'kept-offline' → network is down; 'stale' → signed out. Stop the pass.
  }
  return result;
}
